import { z } from 'zod'
import * as React from 'react'
import {
  ColumnDef,
  ColumnFiltersState,
  getCoreRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getSortedRowModel,
  SortingState,
  useReactTable,
  VisibilityState,
} from '@tanstack/react-table'
import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  type UniqueIdentifier,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import { arrayMove, SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { Tabs, TabsContent } from '@/components/ui/tabs'
import { IconPlus } from '@tabler/icons-react'
import { restrictToVerticalAxis } from '@dnd-kit/modifiers'
import { Table, TableBody } from '@/components/ui/table'
import DragHandle from './DragHandle'
import DraggableRow from './DraggableRow'
import { TableCellViewer } from './TableCellViewer'
import { useMemo } from 'react'
import { useSocketContextData } from '@/context/socket'
import { PowerDocsEventsName } from '@/types/SocketEvents'
import { useParams } from 'react-router-dom'

export default function DataTable({
  data: initialData,
  variant,
  loadDocument,
  setDoc,
}: {
  data: z.infer<any>[]
  isLoading: boolean
  variant: string
  setDoc: (value: any) => void
  loadDocument: () => void
}) {
  const { io } = useSocketContextData()
  const columns: ColumnDef<z.infer<any>>[] = useMemo(
    () => [
      {
        id: 'drag',
        header: () => null,
        cell: ({ row }) => <DragHandle id={row.original.id} />,
        enableSorting: false,
      },
      {
        accessorKey: 'title',
        header: 'Title',
        cell: ({ row }) => {
          return <TableCellViewer item={row.original} />
        },
        enableHiding: false,
        enableSorting: false,
      },
    ],
    [],
  )

  const [data, setData] = React.useState(() => initialData)

  const [rowSelection, setRowSelection] = React.useState({})
  const [columnVisibility, setColumnVisibility] = React.useState<VisibilityState>({})
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>([])
  const [sorting, setSorting] = React.useState<SortingState>([])

  const sortableId = React.useId()
  const sensors = useSensors(useSensor(MouseSensor, {}), useSensor(TouchSensor, {}), useSensor(KeyboardSensor, {}))

  const dataIds = React.useMemo<UniqueIdentifier[]>(() => data?.map(({ id }) => id) || [], [data, initialData])
  const table = useReactTable({
    data,
    columns,
    state: {
      sorting,
      columnVisibility,
      rowSelection,
      columnFilters,
    },
    getRowId: (row) => row.id.toString(),
    enableRowSelection: true,
    onRowSelectionChange: setRowSelection,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onColumnVisibilityChange: setColumnVisibility,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
  })

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event
    if (active && over && active.id !== over.id) {
      const oldIndex = dataIds.indexOf(active.id)
      const newIndex = dataIds.indexOf(over.id)
      io.emit(PowerDocsEventsName.REORDER_POWERDOC_SECTIONS, {
        variant,
        oldIndex,
        newIndex,
        dragged: active.id,
        dropped: over.id,
        documentUUID: params.documentUUID,
        token: localStorage.getItem('accessToken'),
      })
      setData((data) => {
        const oldIndex = dataIds.indexOf(active.id)
        const newIndex = dataIds.indexOf(over.id)
        const newData = arrayMove(data, oldIndex, newIndex)
        setDoc((prev) => ({
          ...prev,
          [variant.toLowerCase()]: {
            ...prev[variant.toLowerCase()],
            sections: newData,
          },
        }))
        return newData
      })
      loadDocument?.()
    }
  }
  const params = useParams()
  return (
    <Tabs defaultValue="outline" className="w-full flex-col justify-start gap-6">
      <TabsContent value="outline" className="relative flex flex-col gap-4 overflow-auto">
        <div className="overflow-hidden rounded-lg">
          <DndContext
            collisionDetection={closestCenter}
            modifiers={[restrictToVerticalAxis]}
            onDragEnd={handleDragEnd}
            sensors={sensors}
            id={sortableId}
          >
            <Table className="border-0">
              <TableBody className="**:data-[slot=table-cell]:first:w-8">
                {table.getRowModel().rows?.length ? (
                  <>
                    <SortableContext items={dataIds} strategy={verticalListSortingStrategy}>
                      {table.getRowModel().rows.map((row) => (
                        <DraggableRow key={row.id} row={row} />
                      ))}
                    </SortableContext>
                  </>
                ) : (
                  <></>
                )}
              </TableBody>
            </Table>
          </DndContext>
        </div>
        <div className="flex items-center justify-between px-4">
          <div className="text-muted-foreground hidden flex-1 text-sm lg:flex">
            <div className={'flex flex-row gap-1 items-center cursor-pointer hidden'} onClick={console.log}>
              <IconPlus size={20} color={'#3F3F46'} />
              <span className={'text-medium text-[#3F3F46] font-medium'}>Add section</span>
            </div>
          </div>
          <div className="flex w-full items-center gap-8 lg:w-fit"></div>
        </div>
      </TabsContent>
      <TabsContent value="past-performance" className="flex flex-col px-4 lg:px-6">
        <div className="aspect-video w-full flex-1 rounded-lg border border-dashed"></div>
      </TabsContent>
      <TabsContent value="key-personnel" className="flex flex-col px-4 lg:px-6">
        <div className="aspect-video w-full flex-1 rounded-lg border border-dashed"></div>
      </TabsContent>
    </Tabs>
  )
}
